import {
  anyContainingPermission,
  useAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { buildPermission } from "portal/utils/auth";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";

export const cropsBasicReadStatement = anyContainingPermission(
  PermissionAction.read,
  PermissionResource.crops_basic,
  PermissionDomain.customer
);

/** Crop ids, recommended models, and the Veselka link. */
export const cropsAdvancedReadStatement = buildPermission(
  PermissionAction.read,
  PermissionResource.crops_advanced,
  PermissionDomain.all
);

/** Enabling and disabling crops, and editing the recommended model. */
export const cropsAdvancedUpdateStatement = buildPermission(
  PermissionAction.update,
  PermissionResource.crops_advanced,
  PermissionDomain.all
);

export const modelsPinnedUpdateStatement = anyContainingPermission(
  PermissionAction.update,
  PermissionResource.models_pinned,
  PermissionDomain.robot
);

/** Carbon-internal pin when the robot disallows plant-profile model pinning. */
export const plantProfileModelPinningBypassStatement = buildPermission(
  PermissionAction.update,
  PermissionResource.plant_profile_model_pinning_bypass,
  PermissionDomain.all
);

export const veselkaReadStatement = buildPermission(
  PermissionAction.read,
  PermissionResource.veselka,
  PermissionDomain.all
);

export const useCanReadAdvancedCrops = (): boolean =>
  useAuthorizationRequired(cropsAdvancedReadStatement);

export const useCanUpdateAdvancedCrops = (): boolean =>
  useAuthorizationRequired(cropsAdvancedUpdateStatement);

export const useCanPinModels = (): boolean =>
  useAuthorizationRequired(modelsPinnedUpdateStatement);

/** Missing or false matches the robot default: pinning is disallowed. */
export const isPlantProfileModelPinningEnabled = (
  robotFeatureFlags: Record<string, boolean> | undefined
): boolean =>
  Boolean(robotFeatureFlags?.enable_pinning_models_for_plant_profiles_feature);

/**
 * Who may pin a carbon-crop model on this robot.
 * Flag on: models_pinned. Flag off: plant_profile_model_pinning_bypass.
 * The two grants are exclusive. A missing flag is off.
 */
export const useCanPinCarbonCropModel = (
  robotFeatureFlags: Record<string, boolean> | undefined
): boolean => {
  const canPinModels = useCanPinModels();
  const canBypass = useAuthorizationRequired(
    plantProfileModelPinningBypassStatement
  );
  return isPlantProfileModelPinningEnabled(robotFeatureFlags)
    ? canPinModels
    : canBypass;
};

export const useCanViewInVeselka = (): boolean => {
  const canReadAdvanced = useCanReadAdvancedCrops();
  const hasVeselka = useAuthorizationRequired(veselkaReadStatement);
  return canReadAdvanced && hasVeselka;
};
